import { GoogleAnalyticsEventType, GoogleAnalyticsEvent, GoogleAnalyticsScreen } from 'enums/GoogleAnalyticsEventType';
import { OfferType } from 'enums/curo/OfferType';
import { PrescreenedOfferType } from 'enums/curo/PrescreenedOfferType';
import { AvailableOffersTableVariables, CuroApplicationVariables } from 'enums/curo/Variables';
import { TableValue } from 'product_modules/api/Types';
import { ApplicationStatuses, ICuroApplication } from 'types/curo/CuroApplication';

enum EVENT_STATUS {
  CREATED = 'created',
  FUNDED = 'funded',
  APPROVED = 'approved',
  BRANCH = 'branch',
  CONTACT = 'contact',
  EMAIL_VERIFICATION = 'emailVerification',
  PHONE_VERIFICATION = 'phoneVerification',
  MORE_INFO = 'more_info',
  DECLINE = 'decline',
}

type UsedLoanOfferType = 'SL Unsecured' | 'SL Refi' | 'LL Unsecured' | 'LL Secured';

const getVariable = (application: ICuroApplication, key: CuroApplicationVariables) => application?.variables?.[key];

const getMaxLoanOffer = (application: ICuroApplication) => {
  const offers = (getVariable(application, CuroApplicationVariables.AvailableLoanOffers) as TableValue) || [];

  return [...offers].sort((a, b) => 
    (b[AvailableOffersTableVariables.LoanAmount] as number) - (a[AvailableOffersTableVariables.LoanAmount] as number),
  )[0];
};

const getOfferByType = (application: ICuroApplication, loanType: UsedLoanOfferType) => {
  const offers = (getVariable(application, CuroApplicationVariables.AvailableLoanOffers) as TableValue) || [];

  return [...offers].find(offer => offer[AvailableOffersTableVariables.LoanType] === loanType);
};

const getAvailableLoanOfferParams = (application: ICuroApplication) => {
  if (application.status === ApplicationStatuses.Decline) {
    return {
      loanType: '',
      loanAmountApproved: '',
      approvedDate: null,
      status: EVENT_STATUS.DECLINE,
    };
  }

  const offerType = getVariable(application, CuroApplicationVariables.OfferType);

  if (offerType === OfferType.NoCreditOffer) {
    const maxLoanOffer = getMaxLoanOffer(application);

    return {
      loanType: maxLoanOffer?.[AvailableOffersTableVariables.LoanType],
      loanAmountApproved: maxLoanOffer?.[AvailableOffersTableVariables.LoanAmount],
      approvedDate: null,
      status: EVENT_STATUS.MORE_INFO,
    };
  } else if (offerType === OfferType.OnlineOffer) {
    const offer = getOfferByType(application, 'SL Unsecured') || getOfferByType(application, 'SL Refi'); ;

    return {
      loanType: 'SL Unsecured',
      loanAmountApproved: offer?.[AvailableOffersTableVariables.LoanAmount],
      approvedDate: new Date().toISOString(),
      status: EVENT_STATUS.APPROVED,
    };
  } else {
    const offer = 
      getOfferByType(application, 'LL Unsecured') 
      || getOfferByType(application, 'SL Unsecured') 
      || getOfferByType(application, 'LL Secured');

    return {
      loanType: offer?.[AvailableOffersTableVariables.LoanType],
      loanAmountApproved: offer?.[AvailableOffersTableVariables.LoanAmount],
      approvedDate: new Date().toISOString(),
      status: EVENT_STATUS.APPROVED,
    };
  }
};

const getSelectedOfferParams = (application: ICuroApplication) => {
  const selectedOffer = (getVariable(application, CuroApplicationVariables.SelectedLoanOffer) as TableValue)?.[0];

  return {
    loanType: selectedOffer?.[AvailableOffersTableVariables.LoanType],
    loanAmountApproved: selectedOffer?.[AvailableOffersTableVariables.LoanAmount],
  };
};

const DMMatchResultEvent = {
  [PrescreenedOfferType.Online]: GoogleAnalyticsEvent.DmOnlineOffer,
  [PrescreenedOfferType.Branch]: GoogleAnalyticsEvent.DmBranchOffer,
  [PrescreenedOfferType.NoMatch]: GoogleAnalyticsEvent.DmNoMatch,
};

const DmMathchResultStatus = {
  [PrescreenedOfferType.Online]: EVENT_STATUS.CONTACT,
  [PrescreenedOfferType.Branch]: EVENT_STATUS.BRANCH,
  [PrescreenedOfferType.NoMatch]: EVENT_STATUS.CONTACT,
};

const createPageView = (screen: GoogleAnalyticsScreen, isAppExists: boolean = true) => (app: ICuroApplication) => ({
  event: GoogleAnalyticsEvent.PageView,
  screen,
  ...isAppExists && { 
    application_id: getVariable(app, CuroApplicationVariables.ApplicationDisplayId),
  },
});

type EventConfigType = Record<
  GoogleAnalyticsEventType, 
  (
    app: ICuroApplication, 
    extra?: Record<string, any>,
  ) => Record<string, any> | null
>;

export const EVENT_CONFIG: EventConfigType = {
  [GoogleAnalyticsEventType.DmWelcome]: createPageView(GoogleAnalyticsScreen.DmWelcome, false),

  [GoogleAnalyticsEventType.DmMatchResult]: (_, params) => {
    const prescreenedOfferType = params?.prescreenedOfferType as PrescreenedOfferType;

    return {
      event: DMMatchResultEvent[prescreenedOfferType],
      status: DmMathchResultStatus[prescreenedOfferType],
      decisionid: params?.decisionId,
    };
  },

  [GoogleAnalyticsEventType.DmPersonalInfo]: createPageView(
    GoogleAnalyticsScreen.DmPersonalInfo,
    false,
  ),

  [GoogleAnalyticsEventType.DmBranchOfferWithSummit]: (_, params) => ({
    ...createPageView(GoogleAnalyticsScreen.DmBranchOfferWithSummit, false)({} as ICuroApplication),
    summit_lead_id: params?.summitEncryptedLeadId,
  }),

  [GoogleAnalyticsEventType.ApplyProcessPersonalInfo]: createPageView(
    GoogleAnalyticsScreen.ApplyProcessPersonalInfo,
    false,
  ),

  [GoogleAnalyticsEventType.ApplyProcessContactInfo]: () => ({
    ...createPageView(GoogleAnalyticsScreen.ApplyProcessContactInfo, false)({} as ICuroApplication),
    status: EVENT_STATUS.CONTACT,
  }),

  [GoogleAnalyticsEventType.ApplyProcessEmailVerify]: (app) => ({
    ...createPageView(
      GoogleAnalyticsScreen.ApplyProcessEmailVerify)(app),
    status: EVENT_STATUS.EMAIL_VERIFICATION,
    emailVerification: 'no',
    phoneVerification: 'yes',
  }),

  [GoogleAnalyticsEventType.ApplyProcessPhoneVerify]: (app) => ({
    ...createPageView(GoogleAnalyticsScreen.ApplyProcessPhoneVerify)(app),
    status: EVENT_STATUS.PHONE_VERIFICATION,
    emailVerification: 'no',
    phoneVerification: 'no',
  }),

  [GoogleAnalyticsEventType.ApplyProcessIncome]: (app) => ({
    ...createPageView(GoogleAnalyticsScreen.ApplyProcessIncome)(app),
    emailVerification: 'yes',
    phoneVerification: 'yes',
  }),

  [GoogleAnalyticsEventType.ApplyProcessConsent]: createPageView(
    GoogleAnalyticsScreen.ApplyProcessConsent,
  ),

  [GoogleAnalyticsEventType.PrequalCompleted]: (app) => {
    const { loanAmountApproved, loanType, status, approvedDate } = getAvailableLoanOfferParams(app);

    return {
      event: GoogleAnalyticsEvent.PrequalCompleted,
      application_id: getVariable(app, CuroApplicationVariables.ApplicationDisplayId),
      loan_type: loanType,
      loan_amount_approved: loanAmountApproved,
      status: status,
      customer_type: getVariable(app, CuroApplicationVariables.BorrowerStatus),
      ...(approvedDate && { approvedDate: approvedDate }),
    };
  },

  [GoogleAnalyticsEventType.ApplyProcessBranchOffer]: createPageView(
    GoogleAnalyticsScreen.ApplyProcessBranchOffer,
  ),

  [GoogleAnalyticsEventType.ApplyProcessOnlineOffer]: createPageView(
    GoogleAnalyticsScreen.ApplyProcessOnlineOffer,
  ),

  [GoogleAnalyticsEventType.ApplyProcessOnlineOfferRefi]: createPageView(
    GoogleAnalyticsScreen.ApplyProcessOnlineOfferRefi,
  ),

  [GoogleAnalyticsEventType.ApplyProcessPPCJourneyOffer]: createPageView(
    GoogleAnalyticsScreen.ApplyProcessPPCJourneyOffer,
  ),

  [GoogleAnalyticsEventType.ApplyProcessMoreInformation]: createPageView(
    GoogleAnalyticsScreen.ApplyProcessMoreInformation,
  ),

  [GoogleAnalyticsEventType.ApplyProcessBankingCollection]: createPageView(
    GoogleAnalyticsScreen.ApplyProcessBankingCollection,
  ),

  [GoogleAnalyticsEventType.ApplyProcessBankAcctSelect]: createPageView(
    GoogleAnalyticsScreen.ApplyProcessBankAcctSelect,
  ),

  [GoogleAnalyticsEventType.ApplyProcessLoanDocs]: createPageView(
    GoogleAnalyticsScreen.ApplyProcessLoanDocs,
  ),

  [GoogleAnalyticsEventType.ApplyProcessBranchOfferGeneric]: (app) => ({
    ...createPageView(GoogleAnalyticsScreen.ApplyProcessBranchOfferGeneric)(app),
    summit_lead_id: getVariable(app, CuroApplicationVariables.SummitEncryptedLeadId),
  }),

  [GoogleAnalyticsEventType.ApplyProcessCentralBranch]: createPageView(
    GoogleAnalyticsScreen.ApplyProcessCentralBranch,
  ),

  [GoogleAnalyticsEventType.ApplyProcessPhysicalBranch]: createPageView(
    GoogleAnalyticsScreen.ApplyProcessPhysicalBranch,
  ),

  [GoogleAnalyticsEventType.ApplyProcessDecline]: createPageView(
    GoogleAnalyticsScreen.ApplyProcessDecline,
  ),

  [GoogleAnalyticsEventType.ApplyProcessFundingNewLoan]: createPageView(
    GoogleAnalyticsScreen.ApplyProcessFundingNewLoan,
  ),

  [GoogleAnalyticsEventType.ApplyProcessFundingRefi]: createPageView(
    GoogleAnalyticsScreen.ApplyProcessFundingRefi,
  ),

  [GoogleAnalyticsEventType.ApplyProcessAutoPayEnroll]: createPageView(
    GoogleAnalyticsScreen.ApplyProcessAutoPayEnroll,
  ),

  [GoogleAnalyticsEventType.ApplyProcessAutoPaySetup]: createPageView(
    GoogleAnalyticsScreen.ApplyProcessAutoPaySetup,
  ),

  [GoogleAnalyticsEventType.AutopayDecision]: (app) => ({
    event: GoogleAnalyticsEvent.AutopayDecision,
    application_id: getVariable(app, CuroApplicationVariables.ApplicationDisplayId),
    autopaydecision: getVariable(app, CuroApplicationVariables.AutoPay),
  }),

  [GoogleAnalyticsEventType.OnlineOrigination]: (app) => {
    const loanParams =
      getVariable(app, CuroApplicationVariables.OfferType) === OfferType.OnlineOffer
        ? getSelectedOfferParams(app)
        : getAvailableLoanOfferParams(app);

    const { loanType, loanAmountApproved } = loanParams;

    return {
      event: GoogleAnalyticsEvent.OnlineOrigination,
      application_id: getVariable(app, CuroApplicationVariables.ApplicationDisplayId),
      loan_type: loanType,
      loan_amount_approved: loanAmountApproved,
      customer_type: getVariable(app, CuroApplicationVariables.BorrowerStatus) || '',
      plaid: 'yes',
      online_origination: getVariable(app, CuroApplicationVariables.AorPass),
    };
  },

  [GoogleAnalyticsEventType.CreatedApp]: (_, params) => ({
    event: GoogleAnalyticsEvent.CreatedApp,
    application_id: params?.applicationDisplayId,
    loan_type: 'GT50',
    created_date: new Date().toISOString(),
    status: EVENT_STATUS.CREATED,
    emailVerification: params?.isEmailVerified ? 'yes' : 'no',
    phoneVerification: params?.isPhoneVerified ? 'yes' : 'no',
  }),

  [GoogleAnalyticsEventType.FundedStatus]: (app) => {
    if (app?.status !== ApplicationStatuses.Funded) return null;

    const { loanType, loanAmountApproved } = getSelectedOfferParams(app);

    return {
      event: GoogleAnalyticsEvent.FundedStatus,
      status: EVENT_STATUS.FUNDED,
      application_id: getVariable(app, CuroApplicationVariables.ApplicationDisplayId),
      loan_type: loanType,
      funded_amount: loanAmountApproved,
      funding_date: getVariable(
        app,
        CuroApplicationVariables.WellsFargoTransactionStatusDateAndTime,
      ),
    };
  },
};
